import classNames from 'classnames';
import { RecipientReferenceType } from 'compliance-api/generated';
import pluralize from 'pluralize';
import { useEffect, useMemo, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import {
  CommunicationRecipientDetail,
  useSendCommunicationMutation,
} from '@/modules/admin-dashboard/queries/communications.ts';
import { useUsersListQuery } from '@/modules/admin-dashboard/queries/users.ts';
import { BaseModalProps, Button, CustomCheckbox, ModalContentWrapper, TextInput } from 'ui';
import { replaceQuillSizeOptionIcons, sanitizeCorrespondenceHtml } from 'ui/utils';
import 'react-quill/dist/quill.snow.css';

interface CommunicationClient {
  id: number;
  name: string;
  email?: string | null;
  correspondence_email?: string | null;
}

interface CreateCommunicationModalProps extends BaseModalProps {
  client?: CommunicationClient;
}

const FontSizeStyle = Quill.import('attributors/style/size') as any;

FontSizeStyle.whitelist = ['10px', '14px', '18px', '24px', '32px'];
Quill.register(FontSizeStyle, true);

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'size',
  'script',
  'header',
  'list',
  'link',
];

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ size: '24px' }, { size: '32px' }],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const CreateCommunicationModal = ({
  open,
  onClose,
  client,
}: CreateCommunicationModalProps) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [extraEmails, setExtraEmails] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>(
    client?.email ? [client.id] : [],
  );
  const { mutateAsync, isLoading } = useSendCommunicationMutation();
  const { data: currentUser } = useCurrentUserQuery();

  const { data: clientsData, isLoading: isClientsLoading } = useClientsListQuery(
    {
      page: 1,
      page_size: 100,
      filter_by_name: '',
    },
    false,
    { cacheKey: 'communications-modal-clients' },
  );

  const { data: usersData, isLoading: isUsersLoading } = useUsersListQuery(
    {
      page: 1,
      page_size: 100,
      filter_by_name: '',
    },
    { cacheKey: 'communications-modal-users' },
  );

  const clients = useMemo(
    () =>
      ((clientsData?.pages || []).flatMap(page => page.data) || []).filter(item => !!item.email),
    [clientsData?.pages],
  );

  const users = useMemo(
    () =>
      ((usersData?.pages || []).flatMap(page => page.data) || []).filter(
        item => !!item.email && item.id !== currentUser?.user.id,
      ),
    [currentUser?.user.id, usersData?.pages],
  );

  useEffect(() => {
    replaceQuillSizeOptionIcons();
  }, []);

  const parsedExtraEmails = useMemo(
    () =>
      extraEmails
        .split(',')
        .map(email => email.trim())
        .filter(Boolean),
    [extraEmails],
  );

  const checkedAllClients = selectedClientIds.length === clients.length && clients.length > 0;
  const indeterminateClients = !!selectedClientIds.length && !checkedAllClients;
  const checkedAllUsers = selectedUserIds.length === users.length && users.length > 0;
  const indeterminateUsers = !!selectedUserIds.length && !checkedAllUsers;

  const additionalRecipientLabels = useMemo(
    () => [
      ...users
        .filter(item => selectedUserIds.includes(item.id))
        .map(item => item.email)
        .filter((item): item is string => Boolean(item)),
      ...clients
        .filter(item => selectedClientIds.includes(item.id))
        .map(item => item.email)
        .filter((item): item is string => Boolean(item)),
      ...parsedExtraEmails,
    ],
    [clients, parsedExtraEmails, selectedClientIds, selectedUserIds, users],
  );
  const additionalRecipientDetails = useMemo<CommunicationRecipientDetail[]>(
    () => [
      ...users
        .filter(item => selectedUserIds.includes(item.id) && item.email)
        .map(item => ({
          type: RecipientReferenceType.User,
          label:
            [item.first_name, item.last_name].filter(Boolean).join(' ') ||
            item.email ||
            `User #${item.id}`,
          email: item.email,
          user_id: item.id,
        })),
      ...clients
        .filter(item => selectedClientIds.includes(item.id) && item.email)
        .map(item => ({
          type: RecipientReferenceType.Client,
          label: item.name || item.email || `Client #${item.id}`,
          email: item.email,
          client_id: item.id,
        })),
      ...parsedExtraEmails.map(email => ({
        type: RecipientReferenceType.External,
        label: email,
        email,
      })),
    ],
    [clients, parsedExtraEmails, selectedClientIds, selectedUserIds, users],
  );

  const handleToggleClient = (clientId: number, checked: boolean) => {
    setSelectedClientIds(current =>
      checked ? [...current, clientId] : current.filter(id => id !== clientId),
    );
  };

  const handleToggleUser = (userId: number, checked: boolean) => {
    setSelectedUserIds(current =>
      checked ? [...current, userId] : current.filter(id => id !== userId),
    );
  };

  const handleToggleAllClients = (checked: boolean) => {
    setSelectedClientIds(checked ? clients.map(item => item.id) : []);
  };

  const handleToggleAllUsers = (checked: boolean) => {
    setSelectedUserIds(checked ? users.map(item => item.id) : []);
  };

  const formattedContent = useMemo(() => {
    const sanitizedContent = sanitizeCorrespondenceHtml(content);

    return `<div style="text-align: initial">
        ${sanitizedContent
          .replaceAll('<p>', "<p style='margin: 0'>")
          .replaceAll('<h1>', "<h1 style='margin: 0'>")
          .replaceAll('<h2>', "<h2 style='margin: 0'>")
          .replaceAll('<ul>', "<ul style='margin: 0'>")
          .replaceAll('<li>', "<li style='margin: 0'>")}
      </div>`;
  }, [content]);

  const canSubmit =
    !!subject.trim() &&
    !!stripHtml(content) &&
    !!(selectedUserIds.length || selectedClientIds.length || parsedExtraEmails.length);

  return (
    <ModalContentWrapper
      open={open}
      onClose={onClose}
      title="New Email"
      classes={{ dialogPanel: 'lg:!max-w-5xl' }}
    >
      <div className="grid lg:grid-cols-2 gap-4 lg:gap-0 mt-2">
        <div className="space-y-4 lg:pr-6 lg:border-r">
          <TextInput
            name="subject"
            label="Subtitle"
            value={subject}
            onChange={event => setSubject(event.target.value)}
            placeholder="Some subject"
            required
            disabled={isLoading}
          />
          <ReactQuill
            value={content}
            modules={modules}
            formats={formats}
            theme="snow"
            onChange={setContent}
            readOnly={isLoading}
            className="[&>.ql-container]:min-h-[250px] [&>.ql-container>.ql-editor]:min-h-[250px]"
          />
          <Button
            loading={isLoading}
            disabled={!canSubmit || isLoading}
            onClick={() => {
              void mutateAsync({
                subject: subject.trim(),
                content: formattedContent,
                recipient_user_ids: selectedUserIds,
                recipient_client_ids: selectedClientIds,
                recipient_emails: parsedExtraEmails,
                client_id: client?.id,
                optimistic_recipients: additionalRecipientLabels,
                optimistic_recipient_details: additionalRecipientDetails,
              });
              onClose();
            }}
          >
            Confirm & send
          </Button>
        </div>
        <div className="space-y-4 lg:pl-6">
          {!!client?.correspondence_email && (
            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-sm text-[#374151]">
              Tracking address: <span className="font-medium">{client.correspondence_email}</span>
            </div>
          )}
          <TextInput
            name="recipient_emails"
            label="Additional email addresses"
            value={extraEmails}
            onChange={event => setExtraEmails(event.target.value)}
            placeholder="name@example.com, another@example.com"
            disabled={isLoading}
          />

          <div className="space-y-2">
            <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
              Clients
            </div>
            <div className="max-h-[220px] relative overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-[2]">
                  <tr className="border-b bg-gray-100 text-gray-500 py-2 h-9">
                    <td className="w-11 pl-3">
                      <CustomCheckbox
                        checked={checkedAllClients}
                        indeterminate={indeterminateClients}
                        className={classNames(
                          '[&_.ant-checkbox-inner]:w-5',
                          '[&_.ant-checkbox-inner]:h-5',
                          '[&_.ant-checkbox-inner]:!rounded-sm',
                          '[&_span:not(.ant-checkbox-checked)>.ant-checkbox-inner]:bg-gray-100',
                        )}
                        disabled={isLoading || isClientsLoading || !clients.length}
                        onChange={e => handleToggleAllClients(e.target.checked)}
                      />
                    </td>
                    <td>
                      {selectedClientIds.length} {pluralize('Client', selectedClientIds.length)}
                    </td>
                    <td className="pr-3">Email</td>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(item => (
                    <tr key={item.id} className="h-[38px] [&:not(:last-child)]:border-b">
                      <td className="w-11 pl-3">
                        <CustomCheckbox
                          checked={selectedClientIds.includes(item.id)}
                          disabled={isLoading || !item.email}
                          onChange={e => handleToggleClient(item.id, e.target.checked)}
                          className="[&_.ant-checkbox-inner]:w-5 [&_.ant-checkbox-inner]:h-5 [&_.ant-checkbox-inner]:!rounded-sm"
                        />
                      </td>
                      <td className="max-w-52 truncate">{item.name}</td>
                      <td className="pr-3 max-w-52 truncate">{item.email || '---'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
              Users
            </div>
            <div className="max-h-[220px] relative overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-[2]">
                  <tr className="border-b bg-gray-100 text-gray-500 py-2 h-9">
                    <td className="w-11 pl-3">
                      <CustomCheckbox
                        checked={checkedAllUsers}
                        indeterminate={indeterminateUsers}
                        className={classNames(
                          '[&_.ant-checkbox-inner]:w-5',
                          '[&_.ant-checkbox-inner]:h-5',
                          '[&_.ant-checkbox-inner]:!rounded-sm',
                          '[&_span:not(.ant-checkbox-checked)>.ant-checkbox-inner]:bg-gray-100',
                        )}
                        disabled={isLoading || isUsersLoading || !users.length}
                        onChange={e => handleToggleAllUsers(e.target.checked)}
                      />
                    </td>
                    <td>
                      {selectedUserIds.length} {pluralize('User', selectedUserIds.length)}
                    </td>
                    <td className="pr-3">Email</td>
                  </tr>
                </thead>
                <tbody>
                  {users.map(item => (
                    <tr key={item.id} className="h-[38px] [&:not(:last-child)]:border-b">
                      <td className="w-11 pl-3">
                        <CustomCheckbox
                          checked={selectedUserIds.includes(item.id)}
                          disabled={isLoading || !item.email}
                          onChange={e => handleToggleUser(item.id, e.target.checked)}
                          className="[&_.ant-checkbox-inner]:w-5 [&_.ant-checkbox-inner]:h-5 [&_.ant-checkbox-inner]:!rounded-sm"
                        />
                      </td>
                      <td className="max-w-52 truncate">
                        {[item.first_name, item.last_name].filter(Boolean).join(' ') || '---'}
                      </td>
                      <td className="pr-3 max-w-52 truncate">{item.email || '---'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ModalContentWrapper>
  );
};

export default CreateCommunicationModal;
